import { computed } from "vue";
import { showAlert } from "@erp/shared/utils/dialog";
import { getErrorMessage } from "@erp/shared/utils/error";
import { S3_FOLDER } from "@erp/shared/api/generated/wrappers/s3";
import { useObtainWriteTabStore } from "../_pinia/write-tab.store";
import { validateBasicInfo, validateCalcCost, validateBOMList, validateDirectInput, } from "@erp/shared/components/ui/Document/_utils/validator";
import { CALCULATION_TYPE } from "@erp/shared/components/ui/Document/_types/calculation-type.type";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { useObtainBasicInfoStore } from "../_pinia/basic-info.store";
import { storeToRefs } from "pinia";
import { useObtainCalcCostInfoStore } from "../_pinia/calc-cost-info.store";
import { setApprovalBody } from "@erp/shared/components/ui/Approval/utils/set-approval-body";
import { uploadFilesWithMapping, } from "@erp/shared/utils/file/upload-file-with-mapping";
import { FILE_CATEGORY } from "@erp/shared/types/file-category.types";
import { APPROVAL_PHASE } from "@erp/shared/components/ui/Approval/approval-phase.types";
import { setProjectCode, setInhouseBidNumber, } from "@erp/shared/components/ui/Document/_utils/code-generator";
import { useObtainStockCostStore } from "../_pinia/stock-cost.store";
import { useObtainAction } from "./useObtainAction";
import { MEMBER_TYPE } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member.types";
import { isChecked } from "@erp/shared/components/ui/Approval/utils/is-checked";
/**
 * @description 견적서 서비스 함수, API 요청 로직이 너무 길어 분리함
 */
export function useObtainService() {
    const writeTabStore = useObtainWriteTabStore();
    const { id, givenName, officePhoneNumber } = useLoginInfo();
    const basicStore = useObtainBasicInfoStore();
    const { confirmId, estimateConfirmationId, inputInhouseBidNumber, memberInfoRef: obtainMemberInfo, basicInfoValidate, inputApprovalFile, inputProjectCode, inputCompanyBidNumber, inputDeliveryCondition, inputDueDate, inputProjectName, inputProjectPeriod, inputRemark, inputBlueprintFile, inputObtainFile, inputOtherFiles, filesInputs, } = storeToRefs(basicStore);
    const { companyAutofill } = basicStore;
    const calcCostStore = useObtainCalcCostInfoStore();
    const { calculationType, calcCostDetailData, laborCosts, calcCostDirectInputData, totalPrice, directInputTotalCost, directInputValidate, } = storeToRefs(calcCostStore);
    const stockCostStore = useObtainStockCostStore();
    const { stockCosts } = storeToRefs(stockCostStore);
    /** 현재 선택된 탭에 따라 계산 데이터 선택 */
    const isMaterial = computed(() => calculationType.value === CALCULATION_TYPE.MATERIAL);
    const { createMutation, updateMutation } = useObtainAction();
    const getNextApprovalPhase = () => {
        const checkerId = obtainMemberInfo.value.find((member) => member.type === MEMBER_TYPE.확인)?.id;
        const approverId = obtainMemberInfo.value.find((member) => member.type === MEMBER_TYPE.승인)?.id;
        if (!checkerId || !approverId) {
            throw new Error("확인자 혹은 승인자 정보가 없습니다.");
        }
        const phase = isChecked(Number(id.value), Number(checkerId), Number(approverId))
            ? APPROVAL_PHASE.미승인
            : APPROVAL_PHASE.미확인;
        return phase;
    };
    const buildUploadTasks = () => {
        const uploadTasks = [];
        if (inputApprovalFile.value?.value) {
            uploadTasks.push({
                file: inputApprovalFile.value.value,
                category: FILE_CATEGORY.승인서,
                path: S3_FOLDER.OBTAIN_APPROVAL,
            });
        }
        if (inputBlueprintFile.value?.value) {
            uploadTasks.push({
                file: inputBlueprintFile.value.value,
                category: FILE_CATEGORY.도면,
                path: S3_FOLDER.OBTAIN_BLUEPRINT,
            });
        }
        if (inputObtainFile.value?.value) {
            uploadTasks.push({
                file: inputObtainFile.value.value,
                category: FILE_CATEGORY.수주서,
                path: S3_FOLDER.OBTAIN_OBTAIN,
            });
        }
        if (inputOtherFiles.value?.value &&
            inputOtherFiles.value.value.length > 0) {
            uploadTasks.push({
                file: inputOtherFiles.value.value,
                category: FILE_CATEGORY.기타,
                path: S3_FOLDER.OBTAIN_ETC,
            });
        }
        return uploadTasks;
    };
    const validateObtainConfirm = async () => {
        writeTabStore.goToBasicInfo();
        const okBasic = await validateBasicInfo(obtainMemberInfo.value, basicInfoValidate.value, filesInputs.value);
        if (!okBasic) {
            return false;
        }
        if (isMaterial.value) {
            calcCostStore.mergeCalcStockCost(stockCosts.value);
        }
        await writeTabStore.goToCalcCost();
        if (isMaterial.value) {
            const okCalc = await validateCalcCost(calcCostDetailData.value);
            if (!okCalc) {
                return false;
            }
            await writeTabStore.goToBOMList();
            const okBOM = await validateBOMList();
            if (!okBOM) {
                return false;
            }
        }
        else {
            const isValid = directInputValidate.value ||
                calcCostStore.triggerDirectInputValidation();
            const okDirectInput = await validateDirectInput(isValid);
            if (!okDirectInput) {
                return false;
            }
        }
        return true;
    };
    const getInhouseBidNumber = async () => {
        const raw = inputInhouseBidNumber.value?.value;
        const meta = inputInhouseBidNumber.value && "chipMeta" in inputInhouseBidNumber.value
            ? inputInhouseBidNumber.value.chipMeta
            : undefined;
        if (meta?.mode === "auto") {
            return setInhouseBidNumber(raw, "/obtain");
        }
        return raw;
    };
    const getProjectCode = async () => {
        const raw = inputProjectCode.value?.value;
        const meta = inputProjectCode.value && "chipMeta" in inputProjectCode.value
            ? inputProjectCode.value.chipMeta
            : undefined;
        if (meta?.mode === "auto") {
            return setProjectCode(raw);
        }
        return raw;
    };
    // 견적서 생성
    const createObtainConfirm = async () => {
        try {
            const isValid = await validateObtainConfirm();
            if (!isValid) {
                return;
            }
            const uploadTasks = buildUploadTasks();
            const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
            if (failedFiles.length > 0) {
                showAlert(`실패한 파일: ${failedFiles.join(", ")}`, "일부 파일 업로드 실패");
            }
            const projectCode = await getProjectCode();
            // 견적서에서 전환: 견적번호 그대로 사용, 독립 생성: sequence 생성
            const inhouseBidNumber = estimateConfirmationId.value
                ? inputInhouseBidNumber.value?.value
                : await getInhouseBidNumber();
            const now = new Date().toISOString();
            const phase = getNextApprovalPhase();
            const isAutoChecked = phase === APPROVAL_PHASE.미승인;
            const approvalData = setApprovalBody({
                phase,
                memberInfo: obtainMemberInfo.value,
            });
            const body = {
                confirmation: {
                    ...approvalData,
                    ...(isAutoChecked && { checkedDate: now }),
                    writeType: isMaterial.value ? 0 : 1,
                    estimateConfirmationId: estimateConfirmationId.value,
                    projectCode: projectCode,
                    inhouseBidNumber: inhouseBidNumber,
                    companyBidNumber: inputCompanyBidNumber.value?.value || "",
                    dueDate: inputDueDate.value?.value || "",
                    deliveryCondition: inputDeliveryCondition.value?.value || "",
                    projectName: inputProjectName.value?.value || "",
                    projectPeriod: inputProjectPeriod.value?.value || null,
                    remark: inputRemark.value?.value || null,
                    companyId: companyAutofill.companyId,
                    companyName: companyAutofill.companyName || "",
                    managerId: companyAutofill.managerId,
                    managerName: companyAutofill.managerName || "",
                    managerEmail: companyAutofill.managerEmail || "",
                    managerPhoneNumber: companyAutofill.managerPhone || "",
                    approvalFile: successfulFiles?.approval?.s3FileName || null,
                    blueprintFile: successfulFiles?.blueprint?.s3FileName || null,
                    obtainFile: successfulFiles?.obtain?.s3FileName || null,
                    etcFiles: successfulFiles?.etcFiles?.s3FileName || [],
                    createrName: givenName.value,
                    createrPhoneNumber: officePhoneNumber.value,
                    totalPrice: isMaterial.value
                        ? totalPrice.value
                        : directInputTotalCost.value,
                },
                calcJson: isMaterial.value
                    ? calcCostDetailData.value
                    : calcCostDirectInputData.value,
                labor: isMaterial.value ? laborCosts.value : [],
                bom: stockCosts.value,
            };
            createMutation.mutate({ body });
        }
        catch (err) {
            showAlert(getErrorMessage(err), "검증 에러");
            return;
        }
    };
    // 견적서 수정
    const updateObtainConfirm = async () => {
        try {
            if (!confirmId.value) {
                throw new Error("수정할 수주서 ID가 유효하지 않습니다.");
            }
            const isValid = await validateObtainConfirm();
            if (!isValid) {
                return;
            }
            const uploadTasks = buildUploadTasks();
            const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
            if (failedFiles.length > 0) {
                showAlert(`실패한 파일: ${failedFiles.join(", ")}`, "일부 파일 업로드 실패");
            }
            const projectCode = inputProjectCode.value?.value;
            const now = new Date().toISOString();
            const phase = getNextApprovalPhase();
            const isAutoChecked = phase === APPROVAL_PHASE.미승인;
            const approvalData = setApprovalBody({
                phase,
                memberInfo: obtainMemberInfo.value,
            });
            const body = {
                modifier: id.value,
                confirmation: {
                    ...approvalData,
                    ...(isAutoChecked && { checkedDate: now }),
                    writeType: isMaterial.value ? 0 : 1,
                    projectCode: projectCode,
                    inhouseBidNumber: inputInhouseBidNumber.value?.value,
                    companyBidNumber: inputCompanyBidNumber.value?.value || "",
                    dueDate: inputDueDate.value?.value || "",
                    deliveryCondition: inputDeliveryCondition.value?.value || "",
                    projectName: inputProjectName.value?.value || "",
                    projectPeriod: inputProjectPeriod.value?.value || null,
                    remark: inputRemark.value?.value || null,
                    companyId: companyAutofill.companyId,
                    companyName: companyAutofill.companyName || "",
                    managerId: companyAutofill.managerId,
                    managerName: companyAutofill.managerName || "",
                    managerEmail: companyAutofill.managerEmail || "",
                    managerPhoneNumber: companyAutofill.managerPhone || "",
                    approvalFile: successfulFiles?.approval?.s3FileName || null,
                    blueprintFile: successfulFiles?.blueprint?.s3FileName || null,
                    obtainFile: successfulFiles?.obtain?.s3FileName || null,
                    etcFiles: successfulFiles?.etcFiles?.s3FileName || [],
                    createrName: givenName.value,
                    createrPhoneNumber: officePhoneNumber.value,
                    totalPrice: isMaterial.value
                        ? totalPrice.value
                        : directInputTotalCost.value,
                },
                calcJson: isMaterial.value
                    ? calcCostDetailData.value
                    : calcCostDirectInputData.value,
                labor: isMaterial.value ? laborCosts.value : [],
                bom: stockCosts.value,
            };
            updateMutation.mutate({ id: confirmId.value, body });
        }
        catch (err) {
            showAlert(getErrorMessage(err), "검증 에러");
            return;
        }
    };
    const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
    return { createObtainConfirm, updateObtainConfirm, isLoading };
}
